import axios from 'axios';

// Direct backend connection for development
const API_BASE_URL = 'http://localhost:8000/api/announcements';

// Enhanced authentication with session + token fallback (mirror announcementsApi)
const getCsrfToken = () => {
  try {
    const m = document.cookie.match(/csrftoken=([^;]+)/);
    return m ? m[1] : '';
  } catch { return ''; }
};

const makeAuthenticatedRequest = async (url, options = {}) => {
  const method = (options.method || 'GET').toUpperCase();
  const needsCsrf = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

  // Try token-based request first
  const token = localStorage.getItem('access_token');
  if (token) {
    try {
      const tokenResponse = await axios({
        ...options,
        url,
        withCredentials: false,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        },
      });
      return tokenResponse;
    } catch (tokenError) {
      console.log('Token-based request failed:', tokenError.response?.status);
    }
  }

  // Try session-based request as fallback
  try {
    const sessionResponse = await axios({
      ...options,
      url,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        ...(needsCsrf ? { 'X-CSRFToken': getCsrfToken() } : {}),
        ...options.headers,
      },
    });
    return sessionResponse;
  } catch (sessionError) {
    const status = sessionError.response?.status;
    if (status === 401 || status === 403) {
      // Return a graceful failure for unauthenticated users
      return {
        data: { results: [], count: 0 },
        status: 200
      };
    }
    throw sessionError;
  }
};

/**
 * User Notifications API Service (for course applications and status changes)
 */
export const notificationsApi = {
  /**
   * Get user's notifications (course applications, status changes, etc.)
   */
  getUserNotifications: async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/user-notifications/`, {
        method: 'GET',
      });
      return {
        success: true,
        data: response.data.results || response.data,
        pagination: response.data.results ? {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
        } : null,
      };
    } catch (error) {
      console.error('Failed to fetch user notifications:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch notifications',
        data: [],
      };
    }
  },

  /**
   * Mark a specific notification as read
   */
  markNotificationAsRead: async (notificationId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `${API_BASE_URL}/user-notifications/${notificationId}/mark-read/`,
        { method: 'POST', data: {} },
      );
      return {
        success: true,
        message: response.data.message || 'Notification marked as read',
      };
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to mark notification as read',
      };
    }
  },

  /**
   * Delete a specific user notification
   */
  deleteNotification: async (notificationId) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/user-notifications/${notificationId}/`, {
        method: 'DELETE',
      });
      return { success: true, message: response.data?.message || 'Notification deleted' };
    } catch (error) {
      console.error('Failed to delete notification:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to delete notification' };
    }
  },

  /**
   * Get user notification statistics
   */
  getUserNotificationStats: async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/user-notifications/stats/`, {
        method: 'GET',
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Failed to fetch user notification stats:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch notification stats',
        data: {
          total_notifications: 0,
          unread_notifications: 0,
          by_type: {},
          by_priority: {},
        },
      };
    }
  },
};

export default notificationsApi;
